import type { AuthClient, BearerAccessToken } from '@rhim/react';
import { isDefined } from '@rhim/utils';
import axios, { AxiosError, AxiosHeaders, AxiosHeaderValue, AxiosInstance, AxiosProgressEvent, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

export type AxiosHeadersType = {
  [key: string]: AxiosHeaderValue;
};
/**
 * We should expose secrets only when such endpoints are called.
 */
const API_ENDPOINT_PREFIX = '/api/';

export enum HTTPStatusCode {
  Unauthorized = 401,
  /**
   * 449 is an arbitrary status code that is sent by the server when the client has not supplied all of the information required to complete the request.
   */
  PleaseRetry = 449,
}

export interface UploadConfig {
  uploadEndpointURL: string;
  formData: FormData;
  abortSignal?: AbortSignal;
  withCredentials?: boolean;
  headers?: { [key: string]: string };
}

/**
 * Axios API class.
 * Creates an Axios instance and adds interceptors for setting/renew the authorization token.
 */
export class AxiosApi {
  axiosApiInstance: AxiosInstance;
  authClient: AuthClient;
  bearerToken: BearerAccessToken | null;
  private authRequestInterceptorId: number | null = null;

  constructor(authClient: AuthClient, headers?: AxiosHeadersType) {
    this.axiosApiInstance = axios.create();
    this.authClient = authClient;

    if (isDefined(headers)) {
      const safeHeaders = AxiosHeaders.from(headers);

      this.axiosApiInstance.defaults.headers.common = safeHeaders;
    }

    this.bearerToken = null;
    this.addInterceptors();
  }

  /**
   * Add interceptors for the axios instance.
   */
  addInterceptors() {
    /**
     * If a request to the API failed because of missing authorization header,
     * refresh the access token and try again. Note: this axios instance must be used to make the call.
     */
    this.axiosApiInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest: AxiosRequestConfig & { _retry?: boolean } = error.config;

        // 401 Unauthorized -> token expired. Let's renew the token and try again
        if (isDefined(error.response) && error.response.status === HTTPStatusCode.Unauthorized && !originalRequest._retry) {
          originalRequest._retry = true;

          this.bearerToken = (await this.authClient.renewToken()).bearerAuthentication;

          return this.axiosApiInstance(originalRequest);
        }

        // 449 Processing on server failed - let's retry once
        if (error.response?.status === HTTPStatusCode.PleaseRetry && !originalRequest._retry) {
          originalRequest._retry = true;
          return this.axiosApiInstance(originalRequest);
        }

        return Promise.reject(error);
      }
    );

    /**
     * Intercept calls to our API and add the `Authorization` header to them.
     *
     * @see https://thedutchlab.com/blog/using-axios-interceptors-for-refreshing-your-api-token
     * @see https://github.com/Flyrell/axios-auth-refresh#readme
     * @see https://gist.github.com/mkjiau/650013a99c341c9f23ca00ccb213db1c
     * @see https://medium.com/swlh/handling-access-and-refresh-tokens-using-axios-interceptors-3970b601a5da
     */
    this.authRequestInterceptorId = this.axiosApiInstance.interceptors.request.use(
      async (config) => {
        if (isDefined(config.url) && config.url.startsWith(API_ENDPOINT_PREFIX) && this.bearerToken == null) {
          try {
            const authInfo = await this.authClient.rehydrate();
            this.setBearerToken(authInfo.bearerAuthentication);
          } catch {
            // Do no reject the request if the user is not authenticated.
            // This is required for the public endpoints (e.g. ContactUs) to be accessible
          }
        }
        return this.setHeaders(config);
      },
      (error) => Promise.reject(error)
    );
  }

  /**
   * Sets the authorization token.
   * @param token Authorization token.
   */
  setBearerToken(token: BearerAccessToken) {
    this.bearerToken = token;
  }

  /**
   * Ejects the auth request interceptor so the Service Worker can manage the Authorization header instead.
   * The response interceptor (401 retry) is kept as a safety net.
   */
  disableAuthRequestInterceptor() {
    if (this.authRequestInterceptorId != null) {
      this.axiosApiInstance.interceptors.request.eject(this.authRequestInterceptorId);
      this.authRequestInterceptorId = null;
    }
  }

  protected setHeaders(config: AxiosRequestConfig): InternalAxiosRequestConfig {
    const rawHeaders = {
      ...config.headers,
      Authorization: isDefined(this.bearerToken) ? this.bearerToken : '',
      'Cookiebot-Tag': 'necessary',
    };

    return {
      ...config,
      headers: AxiosHeaders.from(rawHeaders),
    };
  }

  postUpload<T = void | null>(
    { uploadEndpointURL, formData, abortSignal, headers }: UploadConfig,
    onProgress?: (percent: number, rate?: number, estimated?: number) => void,
    onSuccess?: (data: T) => void,
    onError?: (error: AxiosError) => void
  ): Promise<T> {
    return this.axiosApiInstance
      .post<T>(uploadEndpointURL, formData, {
        headers: {
          'content-type': 'multipart/form-data',
          ...headers,
        },
        signal: abortSignal,
        onUploadProgress: (event: AxiosProgressEvent) => {
          const percent = isDefined(event.total) ? Math.floor((event.loaded / event.total) * 100) : 50;
          const rate = event.rate;
          const estimated = event.estimated;
          onProgress?.(percent, rate, estimated);
        },
      })
      .then((response) => {
        onSuccess?.(response.data);
        return response.data;
      })
      .catch((error) => {
        onError?.(error);
        return Promise.reject(error);
      });
  }
}
